import { Component, ElementRef, inject, ViewChild, OnInit, OnDestroy, Input, input, HostListener, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { AppMenuitem } from './app.menuitem';
import { SidebarService } from '@/core/services/sidebar-state.service';
import { Subject, takeUntil } from 'rxjs';
import { MsalGraphService } from '@/core/services/msal-graph.service';
import { UserService } from '@/core/services/user.service';
import { ProfileType } from '@/types/msal';
import { MenuItem } from 'primeng/api';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { RippleModule } from 'primeng/ripple';
import { TooltipModule } from 'primeng/tooltip';
import { DialogModule } from 'primeng/dialog';
import { DividerModule } from 'primeng/divider';
import { IconField } from "primeng/iconfield";
import { InputIcon } from "primeng/inputicon";

@Component({
    selector: '[app-menu]',
    standalone: true,
    imports: [
    CommonModule,
    AppMenuitem,
    RouterModule,
    FormsModule,
    InputTextModule,
    ButtonModule,
    RippleModule,
    TooltipModule,
    DialogModule,
    DividerModule,
    IconField,
    InputIcon
],
    template: `
        <!-- Search Dialog -->
        <p-dialog 
            [(visible)]="showSearchDialog" 
            [modal]="true"
            [closable]="false"
            [dismissableMask]="true"
            [draggable]="false"
            [showHeader]="false"
            [style]="{width: '90vw', maxWidth: '640px'}"
            [baseZIndex]="9999"
            styleClass="menu-search-dialog"
            contentStyleClass="menu-search-dialog__content"
            footerStyleClass="menu-search-dialog__footer">

            <div class="menu-search-dialog__topbar">
                <div class="menu-search-dialog__input-shell">
                    <i class="pi pi-search menu-search-dialog__input-icon"></i>
                    <input 
                        #searchDialogInput
                        type="text" 
                        pInputText
                        class="menu-search-dialog__input"
                        [(ngModel)]="searchQuery"
                        (input)="onSearchChange()"
                        placeholder="Buscar en el menú..."
                        [attr.aria-label]="'Buscar en el menú'" />
                </div>
                <button 
                    pButton
                    pRipple
                    type="button"
                    icon="pi pi-times"
                    class="menu-search-dialog__close p-button-text p-button-rounded"
                    (click)="closeSearchDialog()"
                    [attr.aria-label]="'Cerrar'"></button>
            </div>

            <div class="menu-search-dialog__body">
                <!-- Empty State - Before Search -->
                <div *ngIf="!searchQuery" class="menu-search-dialog__empty-state">
                    <i class="pi pi-search menu-search-dialog__empty-icon"></i>
                    <h3>Buscar en el menú</h3>
                    <p>Escribe para buscar entre todas las opciones disponibles</p>
                </div>

                <!-- No Results -->
                <div *ngIf="searchQuery && filteredMenuItems.length === 0" class="menu-search-dialog__empty-state">
                    <i class="pi pi-inbox menu-search-dialog__empty-icon"></i>
                    <h3>Sin resultados</h3>
                    <p>No se encontraron opciones con "{{ searchQuery }}"</p>
                    <button 
                        pButton
                        pRipple
                        label="Limpiar búsqueda"
                        icon="pi pi-times"
                        class="menu-search-dialog__clear p-button-sm p-button-text"
                        (click)="clearSearch()"></button>
                </div>

                <!-- Search Results -->
                <div *ngIf="searchQuery && filteredMenuItems.length > 0" class="menu-search-dialog__results">
                    <div class="menu-search-dialog__results-header">
                        {{ getTotalResults() }} {{ getTotalResults() === 1 ? 'Resultado' : 'Resultados' }}
                    </div>
                    
                    <ng-container *ngFor="let item of filteredMenuItems">
                        <!-- Group with children -->
                        <div *ngIf="item.items && item.items.length > 0" class="menu-search-dialog__group">
                            <div class="menu-search-dialog__group-header">
                                <i [class]="item.icon"></i>
                                <span>{{ item.label }}</span>
                            </div>
                            <div class="menu-search-dialog__group-items">
                                <a 
                                    *ngFor="let child of item.items"
                                    [routerLink]="child.routerLink"
                                    class="menu-search-dialog__item"
                                    (click)="closeSearchDialog()">
                                    <i [class]="child.icon" class="menu-search-dialog__item-icon"></i>
                                    <span class="menu-search-dialog__item-label">{{ child.label }}</span>
                                    <i class="pi pi-arrow-right menu-search-dialog__item-arrow"></i>
                                </a>
                            </div>
                        </div>

                        <!-- Single item without children -->
                        <a 
                            *ngIf="!item.items && item.routerLink"
                            [routerLink]="item.routerLink"
                            class="menu-search-dialog__item"
                            (click)="closeSearchDialog()">
                            <i [class]="item.icon" class="menu-search-dialog__item-icon"></i>
                            <span class="menu-search-dialog__item-label">{{ item.label }}</span>
                            <i class="pi pi-arrow-right menu-search-dialog__item-arrow"></i>
                        </a>
                    </ng-container>
                </div>
            </div>

            <ng-template pTemplate="footer">
                <div class="menu-search-dialog__footer-row">
                    <span class="menu-search-dialog__footer-keygroup">
                        <kbd>Esc</kbd>
                        <span>Cerrar</span>
                    </span>
                </div>
            </ng-template>
        </p-dialog>

        <ul class="layout-menu" 
            #menuContainer 
            [class.collapsed]="isCollapsed">
            
            <!-- Search Menu Item - At the top -->
            <li class="layout-root-menuitem search-menu-item">
                <div *ngIf="!isCollapsed" class="layout-menuitem-root-text">
                    <span>Búsqueda</span>
                </div>
                <a 
                    (click)="toggleSearchDialog()"
                    tabindex="0"
                    pRipple
                    [pTooltip]="isCollapsed ? 'Buscar (Ctrl+K)' : ''"
                    [tooltipDisabled]="!isCollapsed"
                    tooltipPosition="right">
                    <i class="pi pi-search layout-menuitem-icon"></i>
                    <span class="layout-menuitem-text" *ngIf="!isCollapsed">
                        Buscar menú
                    </span>
                    <span class="search-kbd" *ngIf="!isCollapsed">⌘K</span>
                </a>
            </li>
            
            <ng-container *ngFor="let item of menuItems(); let i = index">
                <li app-menuitem 
                    *ngIf="!item.separator" 
                    [item]="item" 
                    [index]="i" 
                    [root]="true"
                    [collapsed]="isCollapsed"></li>
                <li *ngIf="item.separator && !isCollapsed" class="menu-separator"></li>
            </ng-container>
        </ul>
    `,
    styles: [`
        /* Mismo estilo de opción que el resto del menú: relleno completo
           en hover/selección, sin indicador de borde lateral. */
        .search-menu-item a {
            position: relative;
            display: flex;
            align-items: center;
            cursor: pointer;
            border-radius: 8px;
            transition: background-color var(--sym-duration-fast) var(--sym-ease);
            text-decoration: none;
            /* Sigue al cromo del sidebar, no al tema (ver _sidebar_vertical.scss) */
            color: var(--menu-item-color);
            padding: 0.5rem 1rem;
        }

        .search-menu-item a:hover {
            background-color: var(--menu-hover-bg);
            color: var(--menu-hover-color);
        }

        .search-menu-item a:hover .layout-menuitem-icon {
            color: var(--menu-hover-color);
        }

        .search-menu-item .layout-menuitem-icon {
            color: var(--sidebar-section-color);
        }

        /* El chip ⌘K también es cromo: con --sym-bg/--sym-border quedaba como un
           recorte blanco sobre los esquemas navy/degradado/primary. */
        .search-kbd {
            margin-left: auto;
            padding: 0.25rem 0.5rem;
            font-size: 0.75rem;
            font-family: var(--sym-font-mono);
            background: var(--menu-hover-bg);
            border: 1px solid var(--sidebar-divider);
            border-radius: var(--sym-radius-sm);
            color: var(--sidebar-section-color);
        }

        ::ng-deep .menu-search-dialog .p-dialog {
            border: 1px solid var(--sym-border);
            border-radius: var(--sym-radius-md);
            box-shadow: var(--sym-shadow-e3);
            background: var(--sym-surface);
            overflow: hidden;
        }

        ::ng-deep .menu-search-dialog .p-dialog-header {
            display: none !important;
        }

        ::ng-deep .menu-search-dialog .p-dialog-content {
            padding: 0;
            background: var(--sym-surface);
            color: var(--sym-ink);
            border-radius: var(--sym-radius-md);
        }

        ::ng-deep .menu-search-dialog .p-dialog-footer {
            background: var(--sym-surface);
            border-top: 1px solid var(--sym-border);
            border-radius: var(--sym-radius-md);
            padding: 0.65rem 1rem 0.8rem;
        }

        .menu-search-dialog__content {
            background: var(--sym-surface) !important;
            max-height: 60vh;
            overflow-y: auto;
            border-radius: var(--sym-radius-md);
        }

        .menu-search-dialog__footer {
            background: var(--sym-surface) !important;
            border-top: 1px solid var(--sym-border) !important;
            border-radius: var(--sym-radius-md) !important;
            padding-top: 0.5rem !important;
        }

        .menu-search-dialog__topbar {
            display: flex;
            align-items: center;
            gap: 0.75rem;
            width: 100%;
            background: var(--sym-surface);
            border-bottom: 1px solid var(--sym-border);
            border-radius: var(--sym-radius-md);
            padding: 0.8rem 0.9rem;
            box-sizing: border-box;
            overflow: hidden;
        }

        .menu-search-dialog__input-shell {
            position: relative;
            display: flex;
            align-items: center;
            flex: 1;
            min-width: 0;
        }

        .menu-search-dialog__input {
            width: 100%;
            background: var(--sym-bg) !important;
            border: 1px solid var(--sym-border) !important;
            color: var(--sym-ink) !important;
            border-radius: var(--sym-radius-md);
            padding: 0.72rem 0.9rem 0.72rem 2.5rem !important;
            box-shadow: none;
            min-height: 40px;
            line-height: 1.2;
        }

        .menu-search-dialog__input::placeholder {
            color: var(--sym-gray-500) !important;
        }

        .menu-search-dialog__input:focus {
            border-color: var(--sym-blue-600) !important;
            box-shadow: var(--sym-focus-ring) !important;
        }

        .menu-search-dialog__input-icon {
            position: absolute;
            left: 0.85rem;
            font-size: 0.9rem;
            color: var(--sym-gray-500);
            pointer-events: none;
        }

        .menu-search-dialog__close {
            flex-shrink: 0;
            color: var(--sym-gray-500) !important;
            background: transparent !important;
            border: 0 !important;
            width: 2.2rem;
            height: 2.2rem;
            min-width: 2.2rem;
            border-radius: var(--sym-radius-md);
        }

        .menu-search-dialog__close:hover {
            color: var(--sym-ink) !important;
            background: var(--menu-hover-bg) !important;
        }

        .menu-search-dialog__footer-row {
            display: flex;
            justify-content: center;
            align-items: center;
            width: 100%;
            color: var(--sym-gray-500);
            font-size: 0.75rem;
        }

        .menu-search-dialog__footer-keygroup {
            display: inline-flex;
            align-items: center;
            gap: 0.5rem;
        }

        .menu-search-dialog__footer-keygroup kbd {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            min-width: 2rem;
            height: 1.6rem;
            padding: 0.15rem 0.5rem;
            background: var(--sym-bg);
            border: 1px solid var(--sym-border);
            border-radius: var(--sym-radius-sm);
            color: var(--sym-ink);
            font-family: var(--sym-font-mono);
            font-size: 0.7rem;
            font-weight: 700;
        }

        .menu-search-dialog__close:hover {
            color: var(--sym-ink) !important;
            background: var(--menu-hover-bg) !important;
        }

        .menu-search-dialog__body {
            min-height: 300px;
            padding: 1rem;
            background: var(--sym-surface);
            border-radius: var(--sym-radius-md);
        }

        .menu-search-dialog__empty-state {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            min-height: 260px;
            text-align: center;
            padding: 1.5rem 1rem;
            color: var(--sym-gray-500);
        }

        .menu-search-dialog__empty-state h3 {
            margin: 0 0 0.5rem;
            font-size: 1.05rem;
            font-weight: 700;
            color: var(--sym-ink);
        }

        .menu-search-dialog__empty-state p {
            margin: 0;
            color: var(--sym-gray-500);
            font-size: 0.875rem;
        }

        .menu-search-dialog__empty-icon {
            font-size: 3rem;
            margin-bottom: 1rem;
            color: var(--sym-gray-400);
            opacity: 0.7;
        }

        .menu-search-dialog__clear {
            margin-top: 1rem;
            color: var(--sym-ink) !important;
        }

        .menu-search-dialog__results {
            display: flex;
            flex-direction: column;
            gap: 0.5rem;
        }

        .menu-search-dialog__results-header {
            padding: 0.375rem 0.5rem 0.75rem;
            color: var(--sym-gray-500);
            font-size: 0.69rem;
            font-weight: 700;
            letter-spacing: 0.08em;
            text-transform: uppercase;
        }

        .menu-search-dialog__group {
            margin-bottom: 0.75rem;
        }

        .menu-search-dialog__group-header {
            display: flex;
            align-items: center;
            gap: 0.625rem;
            padding: 0.625rem 0.75rem;
            border-radius: var(--sym-radius-md);
            background: var(--sym-bg);
            border: 1px solid var(--sym-border);
            color: var(--sym-ink);
            font-size: 0.72rem;
            font-weight: 700;
            letter-spacing: 0.08em;
            text-transform: uppercase;
        }

        .menu-search-dialog__group-header i {
            color: var(--sym-blue-600);
        }

        .menu-search-dialog__group-items {
            display: flex;
            flex-direction: column;
            gap: 0.25rem;
            margin-top: 0.5rem;
        }

        .menu-search-dialog__item {
            display: flex;
            align-items: center;
            gap: 0.75rem;
            padding: 0.75rem 0.875rem;
            border-radius: var(--sym-radius-md);
            color: var(--sym-ink);
            text-decoration: none;
            transition: background-color var(--sym-duration-fast) var(--sym-ease), color var(--sym-duration-fast) var(--sym-ease);
        }

        .menu-search-dialog__item:hover {
            background: var(--menu-hover-bg);
            color: var(--menu-hover-color);
            text-decoration: none;
        }

        .menu-search-dialog__item-icon {
            color: var(--sym-gray-500);
            transition: color var(--sym-duration-fast) var(--sym-ease);
        }

        .menu-search-dialog__item:hover .menu-search-dialog__item-icon {
            color: var(--sym-blue-600);
        }

        .menu-search-dialog__item-label {
            flex: 1;
            font-size: 0.875rem;
            font-weight: 500;
        }

        .menu-search-dialog__item-arrow {
            color: var(--sym-gray-400);
            font-size: 0.75rem;
            opacity: 0;
            transition: opacity var(--sym-duration-fast) var(--sym-ease);
        }

        .menu-search-dialog__item:hover .menu-search-dialog__item-arrow {
            opacity: 1;
            color: var(--sym-blue-600);
        }
    `],
    host: {
        class: 'layout-menu-container',
        '[class.collapsed]': 'isCollapsed'
    }
})
export class AppMenu implements OnInit, OnDestroy {
    @Input() collapsed = false;
    menuItems = input<MenuItem[]>();
    model: MenuItem[] = [];
    el: ElementRef = inject(ElementRef);
    private sidebarService = inject(SidebarService);
    private destroy$ = new Subject<void>();

    @ViewChild('menuContainer') menuContainer!: ElementRef;
    @ViewChild('searchDialogInput') searchDialogInput!: ElementRef<HTMLInputElement>;

    isCollapsed = false;
    profileTypeInfo!: ProfileType;
    searchQuery: string = '';
    filteredMenuItems: MenuItem[] = [];
    showSearchDialog: boolean = false;

    constructor(private msalGraphService: MsalGraphService, private userService: UserService) {
        effect(() => {
            this.model = [...(this.menuItems() ?? [])];
            this.filteredMenuItems = [...(this.menuItems() ?? [])];
        });
    }

    ngOnInit() {
        this.sidebarService.isCollapsed$
            .pipe(takeUntil(this.destroy$))
            .subscribe(collapsed => {
                this.isCollapsed = collapsed;
            });
        
        this.getActualProfileType();
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    getActualProfileType() {
        // Usar el nuevo sistema reactivo del UserService
        this.userService.getProfileTypeInfo()
            .pipe(takeUntil(this.destroy$))
            .subscribe(profile => {
                if (profile) {
                    this.profileTypeInfo = profile;
                }
            });
    }

    onSearchChange() {
        if (!this.searchQuery.trim()) {
            this.filteredMenuItems = [...this.model];
            return;
        }

        const query = this.searchQuery.toLowerCase().trim();
        this.filteredMenuItems = this.filterMenuItems(this.model, query);
    }

    clearSearch() {
        this.searchQuery = '';
        this.filteredMenuItems = [...this.model];
    }

    toggleSearchDialog() {
        this.showSearchDialog = !this.showSearchDialog;
        if (this.showSearchDialog) {
            this.searchQuery = '';
            this.filteredMenuItems = [...this.model];
            setTimeout(() => {
                this.searchDialogInput?.nativeElement?.focus();
            }, 100);
        }
    }

    closeSearchDialog() {
        this.showSearchDialog = false;
        this.searchQuery = '';
        this.filteredMenuItems = [...this.model];
    }

    getTotalResults(): number {
        let count = 0;
        for (const item of this.filteredMenuItems) {
            if (item.items && item.items.length > 0) {
                count += item.items.length;
            } else if (item.routerLink) {
                count++;
            }
        }
        return count;
    }

    private filterMenuItems(items: MenuItem[], query: string): MenuItem[] {
        const filtered: MenuItem[] = [];

        for (const item of items) {
            const labelMatches = item.label?.toLowerCase().includes(query);
            
            let matchingChildren: MenuItem[] = [];
            if (item.items && item.items.length > 0) {
                matchingChildren = this.filterMenuItems(item.items, query);
            }

            if (labelMatches || matchingChildren.length > 0) {
                const filteredItem = { ...item };
                
                if (matchingChildren.length > 0) {
                    filteredItem.items = matchingChildren;
                }
                
                filtered.push(filteredItem);
            }
        }

        return filtered;
    }

    @HostListener('window:keydown', ['$event'])
    handleKeyboardShortcut(event: KeyboardEvent) {
        if ((event.ctrlKey || event.metaKey) && event.key === 'k') {
            event.preventDefault();
            this.toggleSearchDialog();
        }
        
        if (event.key === 'Escape' && this.showSearchDialog) {
            this.closeSearchDialog();
        }
    }

}